'use client';

import { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import type { QueryResult, ResultValue, Visualization } from '../lib/api';
import { pivotToSeries } from '../lib/series';

/**
 * Chart component (§6c). Renders a {@link QueryResult} with ECharts, honoring the
 * plan's `visualization` when set (pie / table / bar / line / number) and falling
 * back to the dataviz default otherwise: a time grain → line, a grouping
 * dimension → bar, neither → a single hero number. Colors read from the CSS
 * design tokens so light/dark themes swap without a code path.
 */
/** Max distinct category lines before a multi-series line falls back to a table. */
const MAX_SERIES = 8;

export function Chart({ result }: { result: QueryResult }): React.JSX.Element {
  const ref = useRef<HTMLDivElement>(null);

  const metricCol = result.columns.find((c) => c.kind === 'metric');
  const metricCols = result.columns.filter((c) => c.kind === 'metric');
  const labelCol = result.columns.find((c) => c.kind === 'time' || c.kind === 'dimension');
  const viz: Visualization =
    result.plan.visualization ?? (result.plan.time_grain ? 'line' : labelCol ? 'bar' : 'number');
  // Scatter needs two measures (x vs y) plus a group-by to label each point.
  const renderScatter = viz === 'scatter' && metricCols.length >= 2 && !!labelCol;
  const useEcharts =
    ((viz === 'bar' || viz === 'bar_horizontal' || viz === 'line' || viz === 'pie' || viz === 'donut') &&
      !!metricCol &&
      !!labelCol) ||
    renderScatter;

  // Multi-series: a single measure split across TWO dimensions → one line (or
  // grouped bar) per value of the second dimension. Prefer the time dimension as
  // the x-axis (a trend per category); otherwise the first dimension is the x-axis
  // (a category split by a second category). Beyond MAX_SERIES the chart is noise,
  // so fall back to the table (which still shows every row).
  const dimCols = result.columns.filter((c) => c.kind === 'time' || c.kind === 'dimension');
  const xCol = dimCols.find((c) => c.kind === 'time') ?? dimCols[0];
  const seriesDimCol = dimCols.find((c) => c !== xCol);
  const isChartViz = viz === 'line' || viz === 'bar' || viz === 'bar_horizontal';
  const isMultiSeries = isChartViz && dimCols.length === 2 && !!metricCol && !!xCol && !!seriesDimCol;
  const seriesCount = isMultiSeries
    ? new Set(result.rows.map((r) => String(r[seriesDimCol!.key] ?? ''))).size
    : 0;
  const renderMultiSeries = isMultiSeries && seriesCount >= 2 && seriesCount <= MAX_SERIES;
  const tooManySeries = isMultiSeries && seriesCount > MAX_SERIES;

  useEffect(() => {
    if (!useEcharts || !ref.current || !metricCol || !labelCol) return;

    const token = (name: string): string =>
      getComputedStyle(document.documentElement).getPropertyValue(name).trim();

    const chart = echarts.init(ref.current, null, { renderer: 'svg' });
    const series = token('--series-1');
    const muted = token('--text-muted');
    const surface = token('--surface-1');
    const border = token('--border');
    const textPrimary = token('--text-primary');
    const textSecondary = token('--text-secondary');
    // Categorical palette (design tokens first, then a distinct fallback ramp) —
    // shared by pie slices and multi-series lines.
    const palette = [
      series || '#2f6fed',
      token('--series-2') || '#22a06b',
      '#e8833a',
      '#8b5cf6',
      '#e5484d',
      '#f5c518',
      '#0ea5e9',
      '#d946ef',
      '#14b8a6',
      '#f97316',
      '#64748b',
      '#a3e635',
    ];

    if (renderScatter && labelCol) {
      // Two measures against each other, one point per group-by value. x = the
      // second measure, y = the first (primary); each point labelled by dimension.
      const yM = metricCols[0]!;
      const xM = metricCols[1]!;
      const pts = result.rows.map((r) => ({
        value: [toNumber(r[xM.key]), toNumber(r[yM.key])],
        name: String(r[labelCol.key] ?? ''),
      }));
      chart.setOption({
        color: palette,
        grid: { left: 8, right: 24, top: 24, bottom: 44, containLabel: true },
        tooltip: {
          trigger: 'item',
          backgroundColor: surface,
          borderColor: border,
          textStyle: { color: textPrimary },
          formatter: (p: { data: { name: string; value: [number, number] } }) =>
            `${p.data.name}<br/>${yM.label}: ${p.data.value[1]}<br/>${xM.label}: ${p.data.value[0]}`,
        },
        xAxis: {
          type: 'value',
          name: xM.label,
          nameLocation: 'middle',
          nameGap: 28,
          nameTextStyle: { color: muted },
          axisLine: { lineStyle: { color: token('--baseline') } },
          splitLine: { lineStyle: { color: token('--grid') } },
          axisLabel: { color: muted },
        },
        yAxis: {
          type: 'value',
          name: yM.label,
          nameLocation: 'middle',
          nameGap: 42,
          nameTextStyle: { color: muted },
          splitLine: { lineStyle: { color: token('--grid') } },
          axisLabel: { color: muted },
        },
        series: [
          {
            type: 'scatter',
            data: pts,
            symbolSize: 14,
            itemStyle: { color: series, opacity: 0.85 },
            label: {
              show: true,
              position: 'top',
              color: textSecondary,
              fontSize: 11,
              formatter: (p: { data: { name: string } }) => p.data.name,
            },
          },
        ],
      });
    } else if (viz === 'pie' || viz === 'donut') {
      chart.setOption({
        color: palette,
        tooltip: { trigger: 'item', backgroundColor: surface, borderColor: border, textStyle: { color: textPrimary } },
        legend: { bottom: 0, textStyle: { color: muted } },
        series: [
          {
            type: 'pie',
            radius: ['42%', '70%'],
            center: ['50%', '46%'],
            data: result.rows.map((r) => ({ name: String(r[labelCol.key] ?? ''), value: toNumber(r[metricCol.key]) })),
            // Share of the whole is the point of a pie, and `{d}` is ECharts'
            // own slice percentage — so "count AND percentage by status" is
            // answered by the counts alone. The query layer has no share-of-total
            // measure (a ratio there is per-group, so it can't divide by the
            // grand total); computing it here needs no such capability.
            label: { color: textSecondary, formatter: '{b}: {c} ({d}%)' },
            itemStyle: { borderColor: surface, borderWidth: 2 },
          },
        ],
      });
    } else if (renderMultiSeries && xCol && seriesDimCol) {
      // One series per value of the second dimension: pivot the long
      // (x, series, value) rows into a shared x-axis with a series per value.
      const { categories, series: lines } = pivotToSeries(
        result.rows,
        xCol.key,
        seriesDimCol.key,
        metricCol.key,
      );
      const isBar = viz === 'bar' || viz === 'bar_horizontal';
      chart.setOption({
        color: palette,
        grid: { left: 8, right: 16, top: 30, bottom: 30, containLabel: true },
        legend: { bottom: 0, type: 'scroll', textStyle: { color: muted } },
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: isBar ? 'shadow' : 'line' },
          backgroundColor: surface,
          borderColor: border,
          textStyle: { color: textPrimary },
        },
        xAxis: {
          type: 'category',
          data: categories,
          axisLine: { lineStyle: { color: token('--baseline') } },
          axisTick: { show: false },
          axisLabel: { color: muted },
        },
        yAxis: { type: 'value', splitLine: { lineStyle: { color: token('--grid') } }, axisLabel: { color: muted } },
        series: lines.map((s, i) =>
          isBar
            ? {
                name: s.name,
                type: 'bar',
                data: s.data,
                barMaxWidth: 40,
                itemStyle: { color: palette[i % palette.length], borderRadius: [3, 3, 0, 0] },
              }
            : {
                name: s.name,
                type: 'line',
                data: s.data,
                smooth: false,
                symbolSize: 6,
                connectNulls: true,
                lineStyle: { color: palette[i % palette.length], width: 2 },
                itemStyle: { color: palette[i % palette.length] },
              },
        ),
      });
    } else {
      const isLine = viz === 'line';
      const isHBar = viz === 'bar_horizontal';
      const categories = result.rows.map((r) => String(r[labelCol.key] ?? ''));
      const values = result.rows.map((r) => toNumber(r[metricCol.key]));
      // The two axes: one categorical (the labels), one numeric (the measure). A
      // vertical bar / line puts categories on X; a horizontal bar swaps them —
      // categories on Y, `inverse` so the first row (largest, since the plan sorts
      // desc) sits at the TOP and the bars descend the way the question asked.
      const catAxis = {
        type: 'category' as const,
        data: categories,
        axisLine: { lineStyle: { color: token('--baseline') } },
        axisTick: { show: false },
        axisLabel: { color: muted },
      };
      const valAxis = { type: 'value' as const, splitLine: { lineStyle: { color: token('--grid') } }, axisLabel: { color: muted } };
      chart.setOption({
        grid: { left: 8, right: 16, top: 24, bottom: 8, containLabel: true },
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: isLine ? 'line' : 'shadow' },
          backgroundColor: surface,
          borderColor: border,
          textStyle: { color: textPrimary },
        },
        xAxis: isHBar ? valAxis : catAxis,
        yAxis: isHBar ? { ...catAxis, inverse: true } : valAxis,
        series: [
          isLine
            ? {
                type: 'line',
                data: values,
                smooth: false,
                symbolSize: 8,
                lineStyle: { color: series, width: 2 },
                itemStyle: { color: series },
                areaStyle: { color: series, opacity: 0.08 },
              }
            : isHBar
              ? {
                  type: 'bar',
                  data: values,
                  barMaxWidth: 28,
                  itemStyle: { color: series, borderRadius: [0, 4, 4, 0] },
                  label: { show: true, position: 'right', color: textSecondary },
                }
              : {
                  type: 'bar',
                  data: values,
                  barMaxWidth: 40,
                  itemStyle: { color: series, borderRadius: [4, 4, 0, 0] },
                  label: { show: true, position: 'top', color: textSecondary },
                },
        ],
      });
    }

    const onResize = (): void => chart.resize();
    window.addEventListener('resize', onResize);
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const rerender = (): void => chart.dispose(); // token change → next effect run repaints
    mq.addEventListener('change', rerender);

    return () => {
      window.removeEventListener('resize', onResize);
      mq.removeEventListener('change', rerender);
      chart.dispose();
    };
  }, [result, metricCol, labelCol, viz, useEcharts, renderScatter, renderMultiSeries, xCol?.key, seriesDimCol?.key]);

  // A time series with too many category lines is unreadable — show the full table.
  if ((viz === 'table' || tooManySeries) && metricCol) {
    return <ResultTable result={result} />;
  }
  if (!useEcharts || !metricCol) {
    return <StatTile label={metricCol?.label ?? 'Result'} value={result.rows[0]?.[metricCol?.key ?? ''] ?? '—'} />;
  }
  return <div ref={ref} style={{ width: '100%', height: 300 }} role="img" aria-label={metricCol.label} />;
}

/** Tabular rendering for `visualization: 'table'` (and long grouped results). */
function ResultTable({ result }: { result: QueryResult }): React.JSX.Element {
  const cell = (kind: string): React.CSSProperties => ({
    textAlign: kind === 'metric' ? 'right' : 'left',
    padding: '8px 10px',
    borderBottom: '1px solid var(--border)',
  });
  return (
    <div style={{ overflowX: 'auto', maxHeight: 300, overflowY: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
        <thead>
          <tr>
            {result.columns.map((c) => (
              <th key={c.key} style={{ ...cell(c.kind), color: 'var(--text-muted)', fontWeight: 600 }}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {result.rows.map((r, i) => (
            <tr key={i}>
              {result.columns.map((c) => (
                <td key={c.key} style={cell(c.kind)}>
                  {r[c.key] === null || r[c.key] === undefined ? '—' : String(r[c.key])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Single hero number for count(*)-style results (a chart would be noise). */
export function StatTile({
  label,
  value,
  unit,
}: {
  label: string;
  value: ResultValue;
  unit?: string;
}): React.JSX.Element {
  return (
    <div>
      <div className="muted" style={{ fontSize: 13, marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontSize: 34, fontWeight: 650, lineHeight: 1 }}>
        {value === null || value === undefined ? '—' : String(value)}
        {unit ? <span style={{ fontSize: 16, marginLeft: 4, color: 'var(--text-secondary)' }}>{unit}</span> : null}
      </div>
    </div>
  );
}

function toNumber(v: ResultValue): number {
  return typeof v === 'number' ? v : Number(v) || 0;
}
